import { OutOfDateGuard, refusedOrVersion, withBasedOn } from 'api/keptVersion';
import { leaveSignedOut } from 'utils/signedOut';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';

/**
 * Brand settings store themselves (auto-save; scene 4, D32).
 *
 * They never wait for a publish: what is stored is in use at once, and two
 * things follow the brand by themselves within about ten minutes (the
 * company name on its job pages, the brand details on an Active careers
 * page). That was so the moment Save was pressed, and it stays so.
 *
 * Brand settings have no required fields (5b item 22), so the per-field
 * rule holds (scene 19): a field that cannot be stored keeps its last good
 * value, the others are stored, and the corner says which one was left out.
 */

/** The server's own check on the display link, as the Save button applied it. */
export const displayLinkProblem = (
  link?: string | null
): string | undefined => {
  if (!link) return undefined;
  if (link.length > 191) return 'at most 191 characters';
  try {
    const url = new URL(link.startsWith('http') ? link : `https://${link}`);
    if (!url.hostname.includes('.')) throw new Error('no domain');
    return undefined;
  } catch {
    return 'it has to be a valid URL';
  }
};

/**
 * What of the screen can be stored: the screen itself, with every field
 * that cannot be stored put back to its last stored value. And what the
 * corner says about the fields left out.
 */
export const storableBrand = <
  T extends { name: string; default_display_link?: string }
>(
  work: T,
  stored: T
): { storable: T; note?: string } => {
  const notes: string[] = [];
  const storable = { ...work };

  if (String(work.name ?? '').trim() === '') {
    storable.name = stored.name;
    notes.push('brand name not saved: a brand needs a name');
  }
  const problem = displayLinkProblem(work.default_display_link);
  if (problem) {
    storable.default_display_link = stored.default_display_link;
    notes.push(`display link not saved: ${problem}`);
  }

  return { storable, note: notes.length ? notes.join(' · ') : undefined };
};

export const brandPath = (brandId: number): string =>
  `/v4/brands/${brandId}/defaults`;

/**
 * Stores a brand's settings, quietly, and answers with the numbers of its
 * default locations in the order they were sent (a location added on the
 * screen has no number until it is stored).
 *
 * @throws when the server did not confirm
 */
export const saveBrand = async (
  brandId: number,
  body: Record<string, unknown>,
  signal?: AbortSignal,
  guard?: OutOfDateGuard
): Promise<number[]> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  const response = await fetch(`${base}${brandPath(brandId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
      'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
    },
    body: JSON.stringify(withBasedOn(body, guard?.basedOn)),
    signal,
  });
  if (response.status === 401) {
    if (window.location.pathname !== '/') {
      leaveSignedOut();
    }
    throw new Error('The session has ended.');
  }
  // D10: out of date is not a failure; the person chooses.
  const version = await refusedOrVersion(response);
  if (!response.ok) {
    throw new Error(`The brand was not stored (${response.status}).`);
  }
  const answer = (await response.json().catch(() => ({}))) as {
    success?: boolean;
    data?: { default_locations?: unknown };
  };
  if (answer.success === false) throw new Error('The brand was not stored.');
  guard?.stored(version);

  const numbers = answer.data?.default_locations;
  return Array.isArray(numbers)
    ? numbers.filter((one): one is number => typeof one === 'number')
    : [];
};
